import { useTheme } from '@table-library/react-table-library/theme';
import { Table as ComposedTable, Body, Row, Cell } from '@table-library/react-table-library/table';
import classNames from 'classnames';

import TableActionRow, { TableActionRowProps } from '../TableActionRow/TableActionRow';

import styles from './Table.module.scss';

interface TableProps<T extends { id: string }> {
  columnSizes: string;
  data: T[];
  renderers: React.FC<T>[];
  actionRenderer: React.FC<T>;
  showOnMobile?: boolean;
}

function Table<T extends { id: string }>({
  columnSizes,
  data,
  renderers,
  actionRenderer,
  showOnMobile = false
}: TableProps<T>) {
  const theme = useTheme([
    {
      Table: `--data-table-library_grid-template-columns: ${columnSizes}`,
      ...commonTableStyle
    }
  ]);

  return (
    <div>
      <div className={classNames({ [styles.table]: !showOnMobile })}>
        <ComposedTable
          data={{ nodes: [data] }}
          theme={theme}
          layout={{ custom: true, horizontalScroll: true, isDiv: true }}>
          {() => (
            <Body>
              {data.map((item) => (
                <Row key={item.id} item={{ id: item.id }}>
                  {renderers.map((renderer, i) => (
                    <Cell key={i}>{renderer(item)}</Cell>
                  ))}
                  <Cell>
                    <div className={styles.actions}>{actionRenderer(item)}</div>
                  </Cell>
                </Row>
              ))}
            </Body>
          )}
        </ComposedTable>
      </div>
    </div>
  );
}

interface TableHeaderProps extends TableActionRowProps {
  headers: (string | null)[];
  columnSizes: string;
}

export const TableHeader = ({ headers, columnSizes, ...tableActionRowProps }: TableHeaderProps) => {
  return (
    <div className={styles.headerWrapper}>
      <TableActionRow {...tableActionRowProps} />
      <div className={styles.tableHeader} style={{ gridTemplateColumns: columnSizes }}>
        {headers.map((title, i) => (
          <div key={i}>{title}</div>
        ))}
      </div>
    </div>
  );
};

const commonTableStyle = {
  HeaderCell: `
    align-items: end;
    text-transform: uppercase;
    font-weight: 900;
    font-size: 0.5rem;
  
    &:nth-of-type(1) {
      justify-self: start;
    }
  `,
  BaseCell: `
    padding: 0.75rem 0.5rem;
    text-align: center;

    &[role="gridcell"] {
      margin-bottom: 0.3rem;
    }

    &:nth-of-type(1) {
      padding-left: 1.25rem;
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
      text-align: left;
    }

    &:last-of-type {
      padding-right: 1.25rem;
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      text-align: right;
    }

    strong {
      font-size: 14px;
      max-width: 100%;
      text-overflow: ellipsis;
      overflow: hidden;
      display: block;
    }
    
    a {
      color: #fb7021;
      font-size: 0.75rem;
    }
  `,
  Row: `
    background-color: #E5E5E5;
  `
};

export default Table;
